import { useParams, useNavigate } from 'react-router-dom'
import {
  AutoDepositBadge,
  ButtonVariant,
  CompanyRoleBadge,
  ProfileCompanyBadge,
  DepositWalletsDrawer,
  LoadingPage,
  Company as SharedCompany,
  type CompanyPageData as CompanyData,
  type CompanyStats,
  type DepositWallet,
} from '@valiant/ui-shared'
import {
  useGetCompanyQuery,
  useGetCompanyStatsQuery,
  useGetCompanyDepositWalletsQuery,
  useGetCompanyBalanceQuery,
} from '../generated/graphql'
import type { UserRole } from '@valiant/ui-shared'
import { TransactionsTab } from '../components/tabs/TransactionsTab'
import { VolumesTab } from '../components/tabs/VolumesTab'
import { UsersTab } from '../components/tabs/UsersTab'
import { AddressesTab } from '../components/tabs/AddressesTab'
import { toast } from 'sonner'
import { useState, useEffect, useMemo, useCallback } from 'react'
import { useQueryParams, type QueryParamsState } from '@valiant/ui-shared'
import { useNavigation } from '../contexts/NavigationContext'
import { MainLayout } from '../components/layout/MainLayout'
import { Wallet } from 'lucide-react'
import { FallbackCompanyIcon } from '@valiant/ui-shared'

export function Company() {
  const { companyId } = useParams<{ companyId: string }>()
  const navigate = useNavigate()
  const queryParams = useQueryParams()
  const { selectedGroupId } = useNavigation()
  const [isWalletsDrawerOpen, setIsWalletsDrawerOpen] = useState(false)

  const [{ data: companyData, fetching: companyLoading, error: companyError }] = useGetCompanyQuery(
    {
      variables: { companyId: companyId! },
      pause: !companyId,
    },
  )
  const [{ data: companyStatsData, fetching: companyStatsLoading, error: companyStatsError }] =
    useGetCompanyStatsQuery({
      variables: { companyId: companyId! },
      pause: !companyId,
    })
  const [{ data: walletsData, fetching: walletsLoading }, fetchWallets] =
    useGetCompanyDepositWalletsQuery({
      pause: true, // Only fetch when requested
      variables: { companyId: companyId! },
    })
  // const [{ data: groupsData, fetching: groupsLoading }] = useGetGroupsQuery()

  const [{ data: companyBalanceData }] = useGetCompanyBalanceQuery({
    variables: { companyId: companyId! },
    pause: !companyId,
  })

  // Check if the current company belongs to the selected group
  useEffect(() => {
    if (companyData?.getCompany && selectedGroupId) {
      const companyGroupId = companyData.getCompany.groupId
      if (companyGroupId !== selectedGroupId) {
        // Company doesn't belong to the selected group, redirect to dashboard
        navigate('/', { replace: true })
        return
      }
    }
  }, [companyData, selectedGroupId, navigate])

  // Map GraphQL data to shared component types
  const company: CompanyData | null = useMemo(
    () =>
      companyData?.getCompany
        ? {
            id: companyData.getCompany.id,
            company: companyData.getCompany.company,
            group: companyData.getCompany.group,
            groupId: companyData.getCompany.groupId,
            groupImageUrl: companyData.getCompany.groupImageUrl || undefined,
            imageUrl: companyData.getCompany.imageUrl || undefined,
            tradeType: companyData.getCompany.tradeType as
              | 'CRYPTO'
              | 'CRYPTO_AND_FIAT'
              | 'DEACTIVATED',
            autoApprove: companyData.getCompany.autoApprove,
            parentCompanyId: companyData.getCompany.parentCompanyId || undefined,
            parentCompanyName: companyData.getCompany.parentCompanyName || undefined,
          }
        : null,
    [companyData],
  )

  const companyStats: CompanyStats | null = companyStatsData?.getCompanyStats
    ? {
        flaggedTransactions: companyStatsData.getCompanyStats.flaggedTransactions,
        currentMonthlyVolumes: companyStatsData.getCompanyStats.currentMonthlyVolumes,
        previousMonthlyVolumes: companyStatsData.getCompanyStats.previousMonthlyVolumes,
        registrationNumber: companyStatsData.getCompanyStats.registrationNumber || '',
        director: companyStatsData.getCompanyStats.director || '',
        country: companyStatsData.getCompanyStats.country || '',
        address: companyStatsData.getCompanyStats.address || '',
        website: companyStatsData.getCompanyStats.website || '',
      }
    : null

  const depositWallets: DepositWallet[] = (walletsData?.getCompanyDepositWallets || []).map(
    (wallet) => ({
      currency: wallet.currency,
      chain: wallet.chain,
      address: wallet.address,
    }),
  )

  const handleFetchDepositWallets = useCallback(
    (companyId?: string) => {
      if (companyId) fetchWallets({ companyId })
    },
    [fetchWallets],
  )

  const handleCopyWalletAddress = (address: string) => {
    navigator.clipboard.writeText(address)
    toast.success('Address copied to clipboard!')
  }

  const updateQueryParams = (updates: Partial<QueryParamsState>) => {
    queryParams.updateMultiple(updates)
  }

  const handleOpenWalletsDrawer = useCallback(() => {
    setIsWalletsDrawerOpen(true)
    handleFetchDepositWallets(companyId)
  }, [companyId, handleFetchDepositWallets])

  const badges = useMemo(() => {
    if (!company) return []
    const items = [
      {
        node: <CompanyRoleBadge tradeType={company?.tradeType} />,
        direction: 'LEFT' as const,
      },
      {
        node: <AutoDepositBadge autoApprove={company.autoApprove} />,
        direction: 'LEFT' as const,
      },
    ]

    if (company.parentCompanyId) {
      items.push({
        node: <ProfileCompanyBadge />,
        direction: 'LEFT',
      })
    }

    return items
  }, [company])

  const buttons = useMemo(() => {
    return [
      {
        label: 'View Wallets',
        variant: ButtonVariant.MAIN_ACTION,
        onClick: handleOpenWalletsDrawer,
        icon: <Wallet className="h-3 w-3 md:h-4 md:w-4" />,
      },
    ]
  }, [handleOpenWalletsDrawer])

  if (companyLoading || companyStatsLoading) {
    return <LoadingPage />
  }

  if (companyError || !company) {
    return (
      <MainLayout>
        <div>Company not found</div>
      </MainLayout>
    )
  }

  return (
    <MainLayout
      breadcrumbs={[
        {
          label: company.company,
          imageUrl: company.imageUrl || undefined,
          fallbackImage: <FallbackCompanyIcon />,
        },
      ]}
      modalsComponent={
        <DepositWalletsDrawer
          open={isWalletsDrawerOpen}
          onOpenChange={setIsWalletsDrawerOpen}
          companyName={company.company}
          companyImageUrl={company.imageUrl}
          depositWallets={depositWallets}
          loading={walletsLoading}
          onCopyAddress={handleCopyWalletAddress}
        />
      }
      headerTitle={company.company}
      headerImage={company.imageUrl || <FallbackCompanyIcon />}
      badges={badges}
      buttons={buttons}
    >
      <SharedCompany
        companyId={companyId!}
        company={company}
        companyStats={companyStats}
        companyLoading={companyLoading}
        companyStatsLoading={companyStatsLoading}
        companyStatsError={companyStatsError}
        companyBalance={companyBalanceData?.getCompanyBalance.holdings.map((holding) => ({
          currency: holding.mint.symbol,
          amount: holding.balance,
          usdAmount: holding.usdValue.replace('$', ''),
          image: holding.mint.image || '',
        }))}
        companyBalanceTotalUsdValue={
          companyBalanceData?.getCompanyBalance.totalUsdValue || '$0.00'
        }
        updateQueryParams={updateQueryParams}
        selectedTab={queryParams.tab || 'users'}
        setSelectedTab={(value: string) => {
          // Clear transactions filter when switching to other tabs
          if (value !== 'transactions') {
            queryParams.updateMultiple({
              tab: value,
              status: undefined,
              type: undefined,
              isFlagged: undefined,
            })
          } else {
            queryParams.updateTab(value)
          }
        }}
        UsersTab={UsersTab}
        TransactionsTab={TransactionsTab}
        VolumesTab={VolumesTab}
        AddressesTab={AddressesTab}
        availableRoleFilters={['TRADER', 'GROUP_OWNER']}
        allowedRoles={['TRADER'] as UserRole[]}
      />
    </MainLayout>
  )
}
